/**
 * "Find it near you" (Keiran, Web Dev meeting 13 Oct 2026, after Loaf): a postcode, a town or the buyer's location
 * gives the nearest Swyft store, the nearest partner store, then every other store, nearest first. On a product
 * page only stores showing the viewed model count; colour and size don't matter, but a sofa and its sofa bed feel
 * different, so in a range sold as both each page only counts stores showing that one.
 */
(() => {
  if (customElements.get('find-near-you')) return;

  const PROXY = 'https://storefront-proxy.swyft.workers.dev';
  const LOOKUP = 'https://api.postcodes.io';
  // Ranges sold both as sofas and as sofa beds. Every other range with a sofa bed is only sofa beds.
  const MIXED_RANGES = new Set(['model 02', 'model 10']);
  const FAMILIES = ['dining table', 'dining chair', 'dining bench', 'coffee table', 'console table', 'side table',
    'bedside table', 'chest of drawers', 'occasional chair', 'storage ottoman', 'bar stool', 'tv unit', 'sideboard',
    'storage', 'desk', 'bed', 'chair', 'model'];
  const FAMILY_RE = new RegExp(`^(${FAMILIES.map((f) => f.replace(/ /g, '\\s+')).join('|')})s?\\b`, 'i');
  const CODE_RE = /\b(x?\d{2})\b/i;
  // Partners write a range's full name; our products carry the short one ("Chair 01", "Storage 01").
  const ALIASES = { 'occasional chair': 'chair', 'storage ottoman': 'storage' };
  const DISPLAY_CACHE = 'fny-floors-v1';
  const SAVED = 'fny-q';
  // Visit Us map: the same map style and pins as the page's old map (visit-us.js).
  const MAP_ID = '4cd1405ca8046ae';
  const UK = { lat: 53.0432, lng: -2.109 };
  const OWN_PIN = 'https://cdn.shopify.com/s/files/1/0875/6599/2271/files/swyft-map-icon-dark.png?v=1784131911';
  const PARTNER_PIN = 'https://cdn.shopify.com/s/files/1/0052/8770/6673/files/swyft-map-icon-compressed.png?v=1662471346';
  const DISPLAY_DRAWER = 'StoreDisplayDrawer-visit';

  /**
   * Measurement (Keiran, 19 Oct 2026): one Shopify analytics event, find_near_you, with an action, for the pixel and
   * GA4, and the same as a Clarity event so the numbers show there at once. Never the postcode or location.
   */
  const track = (action, detail = {}) => {
    const product = location.pathname.match(/\/products\/([^/?#]+)/)?.[1] ?? null;
    try { window.Shopify?.analytics?.publish?.('find_near_you', { action, product, page: product ? 'product' : 'visit_us', ...detail }); } catch (e) {}
    try { window.clarity?.('event', `fny_${action}`); } catch (e) {}
  };

  const decode = (s) => String(s ?? '').replace(/&amp;?/g, '&').replace(/&#39;/g, "'").replace(/&quot;/g, '"');
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const store = (fn) => { try { return fn(); } catch { return null; } };

  /**
   * One piece of a range, from a product's model ("Model 10 Sofa Bed") or one entry of a partner's free-text list
   * ("Models 02", "04", "10 3 seater in Chestnut", "02 sofa bed in Pumice"). A bare number takes the family before it.
   */
  function piece(text, family = 'model') {
    const raw = decode(text).trim();
    let t = raw.replace(/^[(&]\s*|^and\s+/i, '').trim();
    const f = FAMILY_RE.exec(t);
    if (f) {
      family = f[1].toLowerCase().replace(/\s+/g, ' ');
      family = ALIASES[family] ?? family;
      t = t.slice(f[0].length);
    } else if (!CODE_RE.test(t.slice(0, 4))) {
      return { family: null };
    }
    const code = CODE_RE.exec(t)?.[1]?.toLowerCase();
    if (!code) return { family };
    return { family, key: `${family} ${code}`, bed: family === 'model' && /bed/i.test(t), text: raw };
  }

  function partnerFloor(lines) {
    const out = [];
    let family = 'model';
    for (const line of lines ?? []) {
      for (const token of decode(line).split(',')) {
        const p = piece(token, family);
        if (p.family) family = p.family;
        if (p.key) out.push(p);
      }
    }
    return out;
  }

  /** A partner's list as a shopper reads it, an entry a line: "04" after "Models 02" becomes "Model 04". */
  function floorLines(lines) {
    return (lines ?? []).map((line) => {
      let t = decode(line).replace(/\s+/g, ' ').trim().replace(/^(&|and)\s+/i, '').replace(/^\((.*)\)$/, '$1').replace(/\.$/, '').trim();
      if (/^x?\d{2}\b/i.test(t)) t = `Model ${t}`;
      t = t.replace(/^models (x?\d{2})\b(?!,)/i, 'Model $1');
      return t.charAt(0).toUpperCase() + t.slice(1);
    }).filter(Boolean);
  }

  function productPiece(model, type) {
    const p = piece(model);
    if (!p.key) return null;
    if (p.family === 'model' && /sofa bed|chair bed/i.test(type ?? '')) p.bed = true;
    return p;
  }

  /** What a store shows of the viewed piece: the matching entries, empty when it shows none. */
  function showing(floor, want) {
    return floor.filter((i) => i.key === want.key && (!MIXED_RANGES.has(want.key) || !!i.bed === !!want.bed));
  }

  function miles(a, b) {
    const r = (d) => (d * Math.PI) / 180;
    const h = Math.sin(r(b.lat - a.lat) / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(r(b.lng - a.lng) / 2) ** 2;
    return 3958.8 * 2 * Math.asin(Math.sqrt(h));
  }

  /** A UK postcode, an outward code ("SW11") or a place name, to a point. */
  async function locate(q) {
    const text = q.trim();
    const compact = text.replace(/\s+/g, '').toUpperCase();
    // A full postcode that isn't found (mistyped, or retired) still has a district: everything but the last three.
    const outward = compact.length > 4 ? compact.slice(0, -3) : compact;
    const get = async (path) => {
      try {
        const res = await fetch(LOOKUP + path);
        return await res.json();
      } catch {
        return null;
      }
    };
    const full = await get(`/postcodes/${encodeURIComponent(text)}`);
    if (full?.result) return { lat: full.result.latitude, lng: full.result.longitude, label: full.result.postcode };
    if (full?.terminated?.latitude) return { lat: full.terminated.latitude, lng: full.terminated.longitude, label: full.terminated.postcode };
    if (/^[A-Z]{1,2}\d/.test(compact)) {
      const district = await get(`/outcodes/${encodeURIComponent(outward)}`);
      if (district?.result) return { lat: district.result.latitude, lng: district.result.longitude, label: district.result.outcode };
    }
    const place = await get(`/places?limit=1&q=${encodeURIComponent(text)}`);
    const p = place?.result?.[0];
    if (p) return { lat: p.latitude, lng: p.longitude, label: p.name_1 };
    return null;
  }

  /** What each own store has on its floor, from the same Storefront proxy the "View in store" drawer reads. */
  async function ownFloors() {
    const cached = store(() => JSON.parse(sessionStorage.getItem(DISPLAY_CACHE)));
    if (cached && Date.now() - cached.at < 3600000) return cached.floors;
    const query = `{ metaobjects(type: "store", first: 100) { nodes { handle field(key: "on_display_products") {
      references(first: 200) { nodes { ... on Product { productType model: metafield(namespace: "swyft", key: "product_model") { value } } } } } } } }`;
    const res = await fetch(PROXY, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query }) });
    const json = await res.json();
    const floors = {};
    for (const node of json?.data?.metaobjects?.nodes ?? []) {
      const products = node.field?.references?.nodes ?? [];
      if (!products.length) continue;
      floors[node.handle] = products.map((p) => productPiece(p.model?.value, p.productType)).filter(Boolean);
    }
    store(() => sessionStorage.setItem(DISPLAY_CACHE, JSON.stringify({ at: Date.now(), floors })));
    return floors;
  }

  class FindNearYou extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.mode = this.dataset.mode;
      this.want = this.mode === 'product' ? productPiece(this.dataset.model, this.dataset.type) : null;
      this.modelName = this.dataset.modelName || 'it';
      this.stores = store(() => JSON.parse(this.querySelector('[data-fny-stores]').textContent)) ?? [];
      for (const s of this.stores) {
        s.onDisplay = s.floor === true;
        s.lines = floorLines(s.models);
        s.floor = s.floor ? null : partnerFloor(s.models);
        s.position = { lat: parseFloat(s.lat), lng: parseFloat(s.lng) };
      }

      this.drawer = this.querySelector('[data-fny-drawer]');
      this.form = this.querySelector('[data-fny-form]');
      this.input = this.querySelector('[data-fny-input]');
      this.error = this.querySelector('[data-fny-error]');
      this.results = this.querySelector('[data-fny-results]') ?? document.querySelector('[data-fny-results-outside]');

      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.search(this.input.value); });
      this.querySelector('[data-fny-locate]').addEventListener('click', () => this.useLocation());
      this.querySelector('[data-fny-open]')?.addEventListener('click', (e) => this.open(e.currentTarget));
      // Which store link was used, from the card or row it's in (results, and the map's info box on Visit Us).
      document.addEventListener('click', (e) => {
        const el = e.target.closest?.('a, button');
        if (!el || !(this.results.contains(el) || el.closest('.fny-map__info'))) return;
        const store = el.dataset.storeHandle || el.closest('[data-fny-handle]')?.dataset.fnyHandle || null;
        if (el.matches('[data-store-display-trigger]')) track('see_on_display', { store });
        else if (el.matches('[data-fny-floor]')) track('partner_list', { store });
        else if (/google\.com\/maps/.test(el.getAttribute('href') || '')) track('directions', { store });
        else if (el.matches('[data-fny-site]')) track(el.dataset.fnySite, { store });
      });

      if (this.drawer) {
        // A fixed panel inside the sticky product column would scroll with it; the body is its proper parent.
        document.body.appendChild(this.drawer);
        this.drawer.querySelectorAll('[data-fny-close]').forEach((b) => b.addEventListener('click', () => this.close()));
        this.drawer.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.close(); });
      }

      this.mapEl = this.mode === 'all' ? document.querySelector('[data-fny-map]') : null;
      if (this.mapEl) {
        this.mapReady = this.initMap().catch(() => null);
        this.results.addEventListener('click', (e) => this.onListClick(e));
        this.results.addEventListener('keydown', (e) => {
          if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('[data-fny-store]')) { e.preventDefault(); this.onListClick(e); }
        });
      }

      const saved = store(() => localStorage.getItem(SAVED));
      if (saved) this.input.value = saved;
      if (saved && this.mode === 'all') this.search(saved, { quiet: true });
      else if (this.mapEl) this.showAll();
    }

    open(trigger) {
      this.trigger = trigger;
      track('open', { model: this.modelName });
      this.drawer.hidden = false;
      requestAnimationFrame(() => this.drawer.classList.add('is-open'));
      document.body.classList.add('fny-locked');
      // The panel, not the input: focusing the input would throw the keyboard up on a phone.
      this.drawer.querySelector('[role="dialog"]').focus({ preventScroll: true });
      if (this.input.value && !this.results.childElementCount) this.search(this.input.value);
    }

    close() {
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('fny-locked');
      setTimeout(() => { this.drawer.hidden = true; }, 250);
      this.trigger?.focus({ preventScroll: true });
    }

    fail(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    useLocation() {
      if (!navigator.geolocation) return this.fail("Your browser can't share your location. Enter a postcode instead.");
      this.fail('');
      this.busy(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => this.show({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: 'your location' }, { reveal: true }),
        () => { this.busy(false); this.fail("We couldn't get your location. Enter a postcode instead."); },
        { timeout: 10000, maximumAge: 600000 },
      );
    }

    async search(q, { quiet = false } = {}) {
      if (!q.trim()) return this.fail('Enter a postcode or town.');
      this.fail('');
      this.busy(true);
      const origin = await locate(q);
      if (!origin) {
        this.busy(false);
        if (this.mapEl) this.showAll();
        if (!quiet) this.fail("We couldn't find that. Check the postcode and try again.");
        return;
      }
      store(() => localStorage.setItem(SAVED, q.trim()));
      this.show(origin, { reveal: !quiet });
    }

    busy(on) {
      this.form.classList.toggle('is-busy', on);
      if (on) this.results.innerHTML = '<p class="fny__loading">Finding stores…</p>';
    }

    async show(origin, { reveal = false } = {}) {
      let floors = null;
      if (this.want) floors = await ownFloors().catch(() => null);

      const rows = this.stores
        .map((s) => {
          const floor = s.floor ?? (floors ? floors[s.handle] ?? [] : null);
          // Without the proxy an own store is shown rather than dropped: they carry most of the range.
          const shows = !this.want ? [] : floor ? showing(floor, this.want) : null;
          return { ...s, shows, miles: miles(origin, s.position) };
        })
        .filter((s) => !this.want || s.shows === null || s.shows.length)
        .sort((a, b) => a.miles - b.miles);

      const own = rows.find((s) => s.own);
      const partner = rows.find((s) => !s.own);
      const rest = rows.filter((s) => s !== own && s !== partner);
      const near = origin.label === 'your location' ? 'you' : origin.label;

      this.busy(false);
      if (!rows.length) {
        this.results.innerHTML = `<p class="fny__empty">${esc(this.modelName)} isn't on display in a store yet. <a href="/pages/visit-us" target="_blank" rel="noopener">See all our stores</a> to try the fabrics and the rest of the range.</p>`;
        return;
      }

      const reset = this.mapEl ? ' <button type="button" class="fny__reset link" data-fny-reset>Show all stores</button>' : '';
      const html = [`<p class="fny__summary">Nearest to <strong>${esc(near)}</strong>${reset}</p>`];
      if (own) html.push(this.card(own, 'Your nearest Swyft store'));
      if (partner) html.push(this.card(partner, 'Your nearest partner store'));
      if (rest.length) {
        // Visit Us lists every store beside the map; elsewhere a short list ends in a link to the page.
        const limit = this.mode === 'all' && !this.mapEl ? 6 : rest.length;
        const heading = this.want ? `Other stores with ${esc(this.modelName)} on display` : 'More stores near you';
        const all = rest.length > limit ? '<p class="fny__all"><a href="#visit-us-partners">See every store</a></p>' : '';
        const more = this.mapEl ? this.showMore(rest.length, 4, `Show ${rest.length - 4} more stores`) : '';
        html.push(`<div class="fny__more"><h3 class="fny__more-title">${heading}</h3><ul class="fny__list${more ? ' fny__list--clip-4' : ''}">${rest.slice(0, limit).map((s) => this.row(s)).join('')}</ul>${more}${all}</div>`);
      }
      this.results.innerHTML = html.join('');
      // A search the shopper made, not the saved one Visit Us repeats on load.
      if (reveal) track('search', { method: origin.label === 'your location' ? 'location' : 'postcode', stores: rows.length, own_store: own?.handle ?? null, partner: partner?.handle ?? null });
      this.registerDisplay();
      if (this.mapEl) {
        this.results.scrollTop = 0;
        this.frameSearch(origin, rows);
        if (reveal) this.revealMap();
      }
    }

    /** Phones only (CSS hides the rest and the button from 750px): a long list ends in a button rather than a scroll. */
    showMore(count, shown, label) {
      return count > shown ? `<button type="button" class="button button--secondary fny__show-more" data-fny-more>${label}</button>` : '';
    }

    unclip() {
      this.results.querySelector('[data-fny-more]')?.remove();
      this.results.querySelectorAll('.fny__list--clip-4, .fny__list--clip-6').forEach((ul) => ul.classList.remove('fny__list--clip-4', 'fny__list--clip-6'));
    }

    /** On a phone the map sits under the search: bring it up so the search visibly did something. */
    revealMap() {
      if (!matchMedia('(max-width: 749px)').matches) return;
      const box = this.mapEl.getBoundingClientRect();
      if (box.top >= 0 && box.bottom <= innerHeight) return;
      const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mapEl.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    }

    /** Visit Us before a search: every store, ours first, every pin on the map. */
    showAll() {
      const byName = (a, b) => a.name.localeCompare(b.name);
      const list = [...this.stores.filter((s) => s.own).sort(byName), ...this.stores.filter((s) => !s.own).sort(byName)];
      this.results.innerHTML = `<p class="fny__summary">All our stores <span class="fny__count">(${list.length})</span></p>
        <ul class="fny__list fny__list--clip-6">${list.map((s) => this.row(s)).join('')}</ul>${this.showMore(list.length, 6, `Show all ${list.length} stores`)}`;
      this.results.scrollTop = 0;
      this.registerDisplay();
      this.frameAll();
    }

    distance(m) {
      if (m < 0.1) return 'Under 0.1 miles';
      return `${m < 10 ? m.toFixed(1) : Math.round(m)} miles`;
    }

    today(s) {
      const hours = (s.hours ?? [])[new Date().getDay()];
      // A day typed as "false" in the store entry is a day nobody filled in.
      if (!hours || /^(true|false|null)$/i.test(hours.trim())) return '';
      return /closed/i.test(hours) ? 'Closed today' : `Open today ${esc(hours)}`;
    }

    directions(s) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${s.lat},${s.lng}`)}`;
    }

    shownText(s) {
      // A partner's own words say what is on the floor ("10 3 seater in Chestnut"); worth showing when they add detail.
      const detail = (s.shows ?? []).map((p) => p.text).find((t) => /[a-z]{3,}/i.test(t.replace(/^models?\b/i, '')));
      return detail ? `On display: ${esc(detail.replace(/^\d/, (d) => `${this.modelName.split(' ')[0]} ${d}`))}` : '';
    }

    floorText(s) {
      return s.lines.length ? `On display: ${esc(s.lines.join(', '))}` : 'A partner store showing part of our range.';
    }

    /** A store's page or website. Off our site, or from a product page, it opens a new tab so the shopper keeps their place. */
    siteLink(s, cls = '') {
      if (!s.url) return '';
      const ours = /(^|\.)swyfthome\.com$/i.test(store(() => new URL(s.url, location.href).hostname) ?? '');
      const target = this.want || !ours ? ' target="_blank" rel="noopener"' : '';
      return `<a${cls ? ` class="${cls}"` : ''} href="${esc(s.url)}"${target} data-fny-site="${s.own ? 'store_details' : 'store_website'}">${s.own ? 'Store details' : 'Store website'}</a>`;
    }

    card(s, label) {
      // An own store with no products set (Swyft x Basus Home) has only the list partners have, so it shows that.
      const note = this.want
        ? (s.own ? `${esc(this.modelName)} is on the floor, with our own team to help you choose.` : this.shownText(s) || `${esc(this.modelName)} is on display here.`)
        : (s.own && (s.onDisplay || !s.lines.length) ? 'Our own showroom, with our team on hand to help.' : this.floorText(s));
      const link = this.siteLink(s, 'fny-card__link');
      return `
        <article class="fny-card${s.own ? ' fny-card--own' : ''}" data-fny-handle="${esc(s.handle)}"${this.pinAttrs(s)}>
          <p class="fny-card__label">${label}${s.own ? '<span class="fny-card__mark" aria-hidden="true">S</span>' : ''}</p>
          ${s.own && s.image ? `<img class="fny-card__image" src="${esc(s.image)}" alt="" loading="lazy" width="900" height="500">` : ''}
          <div class="fny-card__body">
            <div class="fny-card__top">
              <h3 class="fny-card__name">${esc(s.name)}</h3>
              <span class="fny-card__miles">${this.distance(s.miles)}</span>
            </div>
            <p class="fny-card__address">${esc(s.address)}${s.postcode ? `, ${esc(s.postcode)}` : ''}</p>
            ${this.today(s) ? `<p class="fny-card__hours">${this.today(s)}</p>` : ''}
            <p class="fny-card__note">${note}</p>
            <div class="fny-card__actions">
              <a class="fny-card__link" href="${this.directions(s)}" target="_blank" rel="noopener">Get directions</a>
              ${link}
              ${s.onDisplay ? this.displayButton(s) : ''}
            </div>
          </div>
        </article>`;
    }

    row(s) {
      if (this.mapEl) return this.mapRow(s);
      return `
        <li class="fny__row" data-fny-handle="${esc(s.handle)}">
          <div>
            <p class="fny__row-name">${esc(s.name)}${s.own ? ' <span class="fny__tag">Swyft store</span>' : ''}</p>
            <p class="fny__row-address">${esc(s.address)}</p>
          </div>
          <div class="fny__row-side">
            <span class="fny__row-miles">${this.distance(s.miles)}</span>
            <a href="${this.directions(s)}" target="_blank" rel="noopener">Get directions</a>
          </div>
        </li>`;
    }

    /** A row beside the Visit Us map: picks its pin when clicked, so it carries the store's links itself. */
    mapRow(s) {
      const site = s.url
        ? this.siteLink(s)
        : '';
      return `
        <li class="fny__row fny__row--map" data-fny-handle="${esc(s.handle)}"${this.pinAttrs(s)}>
          <div class="fny__row-main">
            <div class="fny__row-top">
              <p class="fny__row-name">${esc(s.name)}${s.own ? ' <span class="fny__tag">Swyft store</span>' : ''}</p>
              ${s.miles != null ? `<span class="fny__row-miles">${this.distance(s.miles)}</span>` : ''}
            </div>
            <p class="fny__row-address">${esc(s.address)}${s.postcode ? `, ${esc(s.postcode)}` : ''}</p>
            ${this.today(s) ? `<p class="fny__row-hours">${this.today(s)}</p>` : ''}
            <div class="fny__row-actions">
              <a href="${this.directions(s)}" target="_blank" rel="noopener">Get directions</a>
              ${site}
              ${this.displayButton(s)}
            </div>
            ${this.floorList(s)}
          </div>
        </li>`;
    }

    pinAttrs(s) {
      return this.mapEl ? ` data-fny-store="${esc(s.handle)}" tabindex="0" aria-label="Show ${esc(s.name)} on the map"` : '';
    }

    /** Our stores open the drawer of products, on Visit Us and product pages alike; a partner, which only has a list,
     * opens the list in place beside the map (on a product page its card already says what it shows). */
    displayButton(s, trigger = 'data-store-display-trigger') {
      if (s.onDisplay) return `<button type="button" class="link fny__display" ${trigger}="${DISPLAY_DRAWER}" data-store-handle="${esc(s.handle)}">See what's on display</button>`;
      if (!this.mapEl || !s.lines.length) return '';
      return '<button type="button" class="link fny__display" aria-expanded="false" data-fny-floor>See what\'s on display</button>';
    }

    floorList(s) {
      if (s.onDisplay || !s.lines.length) return '';
      return `<ul class="fny__floor" hidden>${s.lines.map((l) => `<li>${esc(l)}</li>`).join('')}</ul>`;
    }

    toggleFloor(button, box) {
      const list = box?.querySelector('.fny__floor');
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? 'Hide what\'s on display' : 'See what\'s on display';
      if (list) list.hidden = !open;
    }

    registerDisplay() {
      customElements.whenDefined('store-display-drawer').then(() => document.getElementById(DISPLAY_DRAWER)?.registerTriggers?.());
    }

    /* The Visit Us map */

    async initMap() {
      if (!window.google?.maps?.importLibrary) return;
      const { Map, InfoWindow } = await google.maps.importLibrary('maps');
      const { LatLngBounds } = await google.maps.importLibrary('core');
      const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
      this.gm = { LatLngBounds, AdvancedMarkerElement };
      this.map = new Map(this.mapEl, {
        center: UK, zoom: 6, mapId: MAP_ID,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
        // Whole zoom steps leave the UK with France beside it on a phone; fractional ones fit it to the box.
        isFractionalZoomEnabled: true,
      });
      this.info = new InfoWindow();
      this.info.addListener('closeclick', () => this.activate(null));
      this.pins = {};
      for (const s of this.stores) {
        if (!Number.isFinite(s.position.lat) || !Number.isFinite(s.position.lng)) continue;
        const icon = document.createElement('img');
        icon.src = s.own ? OWN_PIN : PARTNER_PIN;
        icon.alt = '';
        icon.width = 25;
        icon.height = 35;
        icon.className = 'fny-map__pin';
        const pin = new AdvancedMarkerElement({ map: this.map, position: s.position, title: s.name, content: icon, zIndex: s.own ? 2 : 1 });
        pin.addListener('click', () => this.focusStore(s.handle, { fromMap: true }));
        this.pins[s.handle] = pin;
      }
      // Whatever the list asked for before the map was ready.
      const pending = this.pending;
      this.pending = null;
      pending?.();
    }

    /** Runs now if the map is up, or once it is; only the latest request counts. */
    whenMap(fn) {
      if (this.map) fn();
      else this.pending = fn;
    }

    /**
     * Fit the map to points. With a centre, each point is mirrored through it, so the centre stays put and every
     * point still shows. The top padding is a pin's height: markers stand on their point.
     */
    frame(points, maxZoom, center = null) {
      const bounds = new this.gm.LatLngBounds();
      if (center) bounds.extend(center);
      for (const p of points) {
        bounds.extend(p);
        if (center) bounds.extend({ lat: 2 * center.lat - p.lat, lng: 2 * center.lng - p.lng });
      }
      this.map.fitBounds(bounds, { top: 44, right: 16, bottom: 16, left: 16 });
      google.maps.event.addListenerOnce(this.map, 'idle', () => {
        if (this.map.getZoom() > maxZoom) this.map.setZoom(maxZoom);
      });
    }

    frameAll() {
      this.whenMap(() => {
        this.info.close();
        if (this.you) this.you.map = null;
        // Every pin, as the old map showed the UK. Centring on London as well would need half of France in view.
        this.frame(this.stores.filter((s) => this.pins[s.handle]).map((s) => s.position), 9);
      });
    }

    frameSearch(origin, rows) {
      this.whenMap(() => {
        this.info.close();
        if (this.you) this.you.map = null;
        const dot = document.createElement('span');
        dot.className = 'fny-map__you';
        this.you = new this.gm.AdvancedMarkerElement({ map: this.map, position: origin, title: 'Your search', content: dot, zIndex: 3 });
        // The three nearest stores frame the search; the rest are a pan away.
        this.frame(rows.slice(0, 3).map((s) => s.position), 11, { lat: origin.lat, lng: origin.lng });
      });
    }

    onListClick(e) {
      const more = e.target.closest('[data-fny-more]');
      if (more) {
        // Focus the first row that was hidden, so a keyboard user carries on from where the list stopped.
        const list = more.previousElementSibling;
        const next = list?.children[list.classList.contains('fny__list--clip-4') ? 4 : 6];
        this.unclip();
        next?.focus({ preventScroll: true });
        return;
      }
      if (e.target.closest('[data-fny-reset]')) {
        store(() => localStorage.removeItem(SAVED));
        this.input.value = '';
        this.fail('');
        this.showAll();
        return;
      }
      const floor = e.target.closest('[data-fny-floor]');
      if (floor) return this.toggleFloor(floor, floor.closest('[data-fny-store]'));
      if (e.target.closest('a, button')) return;
      const item = e.target.closest('[data-fny-store]');
      if (item) this.focusStore(item.dataset.fnyStore);
    }

    focusStore(handle, { fromMap = false } = {}) {
      const s = this.stores.find((x) => x.handle === handle);
      const pin = this.pins?.[handle];
      this.activate(handle, fromMap);
      if (!s || !pin) return;
      if (!fromMap) {
        this.map.panTo(s.position);
        if (this.map.getZoom() < 10) this.map.setZoom(10);
        // On a phone the list is under the map: bring the map back into view to show what was picked.
        const box = this.mapEl.getBoundingClientRect();
        if (box.top < 0 || box.bottom > innerHeight) this.mapEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      const hours = this.today(s);
      const site = this.siteLink(s);
      const box = document.createElement('div');
      box.className = 'fny-map__info';
      box.dataset.fnyHandle = s.handle;
      // Not the drawer's trigger attribute: the drawer binds those each time the list redraws, and would open twice.
      box.innerHTML = `
          <p class="fny-map__info-name">${esc(s.name)}</p>
          <p>${esc(s.address)}${s.postcode ? `, ${esc(s.postcode)}` : ''}</p>
          ${hours ? `<p>${hours}</p>` : ''}
          <p class="fny-map__info-links"><a href="${this.directions(s)}" target="_blank" rel="noopener">Get directions</a>${site}</p>
          ${this.displayButton(s, 'data-fny-drawer')}
          ${this.floorList(s)}`;
      box.querySelector('[data-fny-floor]')?.addEventListener('click', (e) => {
        this.toggleFloor(e.currentTarget, box);
        // Opening again makes Google pan the map to fit the taller popup.
        this.info.open({ map: this.map, anchor: pin, shouldFocus: false });
      });
      box.querySelector('[data-fny-drawer]')?.addEventListener('click', (e) => document.getElementById(DISPLAY_DRAWER)?.open?.(e.currentTarget));
      this.info.setContent(box);
      this.info.open({ map: this.map, anchor: pin, shouldFocus: false });
    }

    activate(handle, scroll = false) {
      for (const el of this.results.querySelectorAll('[data-fny-store].is-active')) el.classList.remove('is-active');
      for (const [h, pin] of Object.entries(this.pins ?? {})) pin.content.classList.toggle('is-active', h === handle);
      if (!handle) return;
      const item = this.results.querySelector(`[data-fny-store="${CSS.escape(handle)}"]`);
      if (!item) return;
      if (!item.offsetParent) this.unclip();
      item.classList.add('is-active');
      // Scroll the list, never the page: on a phone the list is under the map and the pin is what they're looking at.
      if (scroll && this.results.scrollHeight > this.results.clientHeight + 1) {
        this.results.scrollTo({ top: item.offsetTop - 12, behavior: 'smooth' });
      }
    }
  }

  customElements.define('find-near-you', FindNearYou);
})();
